import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Check, Ruler } from "lucide-react";
import api, { assetUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import SizeGuideModal from "@/components/SizeGuideModal";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [added, setAdded] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((r) => {
        setP(r.data);
        const firstAvail = (r.data.sizes || []).find((s) => s.stock > 0);
        setSelectedSize(firstAvail?.name || null);
      })
      .catch(() => setP(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center py-32 text-white/40">Chargement…</div>;
  if (!p)
    return (
      <div className="text-center py-32">
        <p className="text-white/40 mb-4">Produit introuvable</p>
        <Link to="/" className="btn-ghost">Retour à la boutique</Link>
      </div>
    );

  const handleAdd = () => {
    if (!selectedSize) return;
    addItem(p, selectedSize, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 lg:py-16">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-white/50 hover:text-magenta text-sm mb-8"
        data-testid="back-to-shop"
      >
        <ArrowLeft size={16} /> Retour à la boutique
      </Link>

      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="aspect-square bg-black border border-white/10 overflow-hidden flex items-center justify-center">
            {p.photos?.[selectedPhoto] ? (
              <img
                src={assetUrl(p.photos[selectedPhoto])}
                alt={p.name}
                className="w-full h-full object-contain"
                data-testid="product-main-photo"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/20">
                No photo
              </div>
            )}
          </div>
          {p.photos?.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {p.photos.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedPhoto(i)}
                  className={`aspect-square border bg-black flex items-center justify-center ${
                    i === selectedPhoto ? "border-magenta" : "border-white/10"
                  }`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={assetUrl(src)} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <span className="text-xs uppercase tracking-widest text-magenta">
            {p.category}
          </span>
          <h1
            className="font-display text-4xl md:text-5xl mt-2 leading-tight"
            data-testid="product-name"
          >
            {p.name}
          </h1>
          <div className="font-display text-3xl text-magenta mt-4" data-testid="product-price">
            {p.price_chf.toFixed(2)} CHF
          </div>

          <p className="text-white/60 mt-6 leading-relaxed whitespace-pre-line">
            {p.description || "Pièce officielle FLUO Neuchâtel."}
          </p>

          {/* Sizes */}
          <div className="mt-8">
            <div className="uppercase text-xs tracking-widest text-white/40 mb-3">
              Taille
            </div>
            <div className="flex items-center flex-wrap justify-between gap-3">
              <div className="flex flex-wrap gap-2">
              {(p.sizes || []).map((s) => {
                const out = s.stock <= 0;
                const active = selectedSize === s.name;
                return (
                  <button
                    key={s.name}
                    disabled={out}
                    onClick={() => setSelectedSize(s.name)}
                    className={`px-4 py-2 border text-sm uppercase tracking-wider transition-all ${
                      out
                        ? "border-white/5 text-white/20 line-through cursor-not-allowed"
                        : active
                        ? "border-magenta bg-magenta text-black"
                        : "border-white/20 hover:border-magenta hover:text-magenta"
                    }`}
                    data-testid={`size-${s.name}`}
                  >
                    {s.name}
                    {!out && s.stock <= 3 && (
                      <span className="ml-1 text-[10px] opacity-60">
                        ({s.stock})
                      </span>
                    )}
                  </button>
                );
              })}
              {(!p.sizes || p.sizes.length === 0) && (
                <span className="text-white/30 text-sm">Aucune taille disponible</span>
              )}
              </div>
              <button
                type="button"
                onClick={() => setSizeGuideOpen(true)}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-white/60 hover:text-magenta transition-colors"
                data-testid="product-size-guide"
              >
                <Ruler size={13} /> Guide des tailles
              </button>
            </div>
          </div>

          {/* Add to cart */}
          <button
            onClick={handleAdd}
            disabled={!selectedSize || added}
            className="btn-fluo mt-10 w-full md:w-auto"
            data-testid="add-to-cart-btn"
          >
            {added ? (
              <>
                <Check size={18} /> Ajouté au panier
              </>
            ) : (
              <>
                <ShoppingBag size={18} /> Ajouter au panier
              </>
            )}
          </button>

          {added && (
            <button
              onClick={() => navigate("/cart")}
              className="btn-ghost mt-3 md:ml-3 w-full md:w-auto"
              data-testid="go-to-cart-btn"
            >
              Voir le panier
            </button>
          )}

          <div className="mt-10 pt-6 border-t border-white/5 text-xs text-white/50 space-y-1">
            <p>· Livraison Suisse via la Poste (PostPac Economy)</p>
            <p>· Paiement sécurisé Stripe (Visa, Mastercard, etc.)</p>
            <p>· Pas besoin de créer un compte</p>
          </div>
        </div>
      </div>
      <SizeGuideModal open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} />
    </div>
  );
}
